import { isDevelopmentEnvironment } from "utils";

const getDebugStatus = () => {
	if (typeof window === "undefined") return false;
	const params = new URLSearchParams(window.location.search);
	if (params.get("chatDebug") === "true") {
		return true;
	}
	return localStorage.getItem("chatDebug") === "true";
};

const IS_DEBUG_ENABLED = getDebugStatus();

export const logChatInfo = (...args) => {
	if (IS_DEBUG_ENABLED) {
		console.log(...args);
	}
};

export const logChatError = (...args) => {
	if (IS_DEBUG_ENABLED) {
		console.error(...args);
	}
};

const chatKeys = [
	"chatEnvironment",
	"chatProactiveToken",
	"preventClearSession",
	"chatDebug",
	"testToken",
	"loadOldChat",
	"devUtilitiesVersion",
	"skipBusinessHoursCheck",
];

export const getNewChatParams = () => {
	let parsedValues = {};
	try {
		const url = new URL(window.location.href);
		const params = new URLSearchParams(url.search);
		chatKeys.forEach((key) => {
			if (params.has(key)) {
				const value = params.get(key);
				if (!!value && value.trim() !== "") {
					window.localStorage.setItem(key, value);
					parsedValues[key] = value;
				}
			} else {
				const storedValue = window.localStorage.getItem(key);
				parsedValues[key] = storedValue;
			}
		});
	} catch (error) {
		logChatError(error);
	}
	return parsedValues;
};

export const endChatSession = async (
	authenticatedChat = false,
	forcedEnd = false
) => {
	if (
		window?.embeddedservice_bootstrap &&
		(forcedEnd ||
			(window.localStorage?.getItem("preventClearSession") !== "true" &&
				authenticatedChat))
	) {
		try {
			await window?.embeddedservice_bootstrap?.userVerificationAPI?.clearSession(
				true
			);
		} catch (error) {
			logChatError(error);
		}
	}
};

const setChatEnabled = (value) => {
	if (value) {
		window.localStorage.setItem("chatEnabled", "true");
	} else {
		window.localStorage.removeItem("chatEnabled");
	}
	return !!value;
};

export const isChatEnabled = ({
	chatEnvironment,
	brand,
	environment,
	locale,
	appConfig,
	loadOldChat,
}) => {
	const brandConfiguration = appConfig?.brands[brand]?.newChatConfiguration;
	if (!brandConfiguration || loadOldChat === "true") {
		return setChatEnabled(false);
	}
	if (brandConfiguration.excludedEnvironments?.includes(environment)) {
		return setChatEnabled(false);
	}
	if (brandConfiguration.enabledRoutes?.includes(window.location.pathname)) {
		return setChatEnabled(true);
	}
	if (
		(chatEnvironment ||
			brandConfiguration.enabledByDefaultEnvironments?.includes(environment)) &&
		(isDevelopmentEnvironment() ||
			brandConfiguration.allowedDomains?.some((allowedDomain) =>
				window.location.origin.endsWith(allowedDomain)
			)) &&
		brandConfiguration.allowedLocales?.includes(locale)
	) {
		return setChatEnabled(true);
	}
	return setChatEnabled(false);
};
export const getChatTokenInformation = (
	OrganizationId,
	getLocalStorageValue,
	decodeJsonWebToken
) => {
	try {
		const chatWebStorage = getLocalStorageValue(
			`${OrganizationId}_WEB_STORAGE`,
			true
		);
		if (chatWebStorage?.JWT) {
			return decodeJsonWebToken(chatWebStorage.JWT);
		}
	} catch (error) {
		logChatError("---> Chat: Error getting chat token information", error);
		return null;
	}
};

export const getChatScriptUrl = (chatConfiguration) => {
	let chatUrl = "";
	if (chatConfiguration) {
		chatUrl = `${chatConfiguration.DeploymentUrl}/assets/js/bootstrap.min.js`;
	}
	return chatUrl;
};

export const dispatchWindowEvent = (name, detail = {}) => {
	window.dispatchEvent(
		new CustomEvent(name, {
			detail,
		})
	);
};

export const loadChatInformation = async ({
	chatEnv,
	token,
	customFetch,
	content,
}) => {
	try {
		const chatInformation = await customFetch(
			`${content.apiUrl.chat}?accessData=true&env=${chatEnv}${
				!!token ? `&token=${token}` : ""
			}`,
			{},
			-1
		);
		return chatInformation;
	} catch (error) {
		logChatError("---> Chat: Error loading chat information", error);
		return {};
	}
};

export const parseBrowser = () => {
	const userAgent = window?.navigator?.userAgent || "";
	try {
		const browserPatterns = [
			{ name: "Edge", regex: /Edg\/([\d.]+)/ },
			{ name: "Chrome", regex: /(?:Chrome|CriOS)\/([\d.]+)/ },
			{ name: "Firefox", regex: /(?:Firefox|FxiOS)\/([\d.]+)/ },
			{ name: "Safari", regex: /Version\/([\d.]+).*Safari/ },
		];
		const osPatterns = [
			{ name: "Windows", regex: /Windows NT ([\d.]+)/ },
			{ name: "iOS", regex: /iPhone OS ([\d_]+)/ },
			{ name: "macOS", regex: /Mac OS X ([\d_]+)/ },
			{ name: "Android", regex: /Android ([\d.]+)/ },
		];
		const browser = browserPatterns.find(({ regex }) => regex.test(userAgent));
		const os = osPatterns.find(({ regex }) => regex.test(userAgent));
		const browserVersion = browser?.["regex"]
			? userAgent.match(browser.regex)?.[1]?.split(".")[0] ?? "Unknown"
			: "Unknown";
		const osVersion = os?.["regex"]
			? userAgent.match(os.regex)?.[1]?.replace(/_/g, ".") ?? "Unknown"
			: "Unknown";
		if (!browser && !os) return `Not identified: ${userAgent}`;
		const browserPart = browser
			? `${browser.name} ${browserVersion}`
			: `Not identified Browser: ${userAgent}`;
		const osPart = os
			? `${os.name} ${osVersion}`
			: `Not identified Os: ${userAgent}`;
		return `${browserPart} / ${osPart}`;
	} catch (e) {
		return `Not identified ${userAgent ? `| ${userAgent}` : ""}`;
	}
};

export const generateMeaningfulTitle = (url) => {
	try {
		const urlObj = new URL(url);
		const fullPath = urlObj.pathname + urlObj.hash;
		const segments = fullPath
			.replace(/[-_]/g, " ")
			.replace(/#/g, "/")
			.split("/")
			.filter((segment) => segment && segment.trim() !== "");
		if (segments.length === 0) return "Home";
		const capitalizedSegments = segments.map((segment) => {
			return segment
				.split(" ")
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
				.join(" ");
		});
		return capitalizedSegments.join(" - ");
	} catch (e) {
		return "Home";
	}
};
